import {
  DataModel, DataModelBuilder, SerializationContext, SerializedData,
} from '../../dto/data-model.js';
import { MapUtils } from '../../../utility/map-utils.js';
import { isSameData } from '../../dto/data-model-utils.js';
import { BusinessModuleRef } from '../business-module-ref.js';
import { OnlineEventTicketOrder } from '../online-event-ticket-order.js';
import { Price } from '../price.js';
import { TimeZone } from '../../../time/timezone.js';
import { GenericOrder } from '../orders/generic-order.js';
import { GenericOrderDonationEntry } from '../orders/generic-order-entries/generic-order-donation-entry.js';
import { CustomerOrderBase } from '../../../contracts/customer-order.js';
import { EventTicketOrder } from '../event-ticket-order.js';

export class DonationPlaceholder extends DataModel {
  static readonly TYPE_ID: string = '65d89fa192244b15a459e5283f59c41a';

  public readonly uniqueId: string;

  private constructor(
    public readonly businessRef: BusinessModuleRef,
    public readonly donation: Price,
    public readonly order: CustomerOrderBase,
  ) {
    super();
    this.uniqueId = order.uniqueId;
  }

  get $typeId(): string {
    return DonationPlaceholder.TYPE_ID;
  }

  private static create(args: {
    businessRef: BusinessModuleRef,
    donation: Price,
    order: CustomerOrderBase,
  }): DonationPlaceholder {
    return new DonationPlaceholder(
        args.businessRef,
        args.donation,
        args.order,
    );
  }

  static fromTicketOrder(order: OnlineEventTicketOrder): DonationPlaceholder | null {
    if(order.donation == null) {
      return null;
    }

    return new DonationPlaceholder(
        order.businessRef.clone(),
        order.donation.clone(),
        order.clone(),
    );
  }

  static fromGenericOrder(order: GenericOrder, entryId: string): DonationPlaceholder | null {
    const entry = order.findEntry(entryId);
    if(entry == null || !GenericOrderDonationEntry.assertType(entry)) {
      return null;
    }

    return new DonationPlaceholder(
        order.businessRef.clone(),
        entry.donation.clone(),
        order.clone(),
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): DonationPlaceholder {
    return DonationPlaceholder.create({
      businessRef: BusinessModuleRef.mustDecode(MapUtils.getString(map, 'businessRef')),
      donation: Price.decode(MapUtils.getString(map, 'donation')),
      order: context.fromData<CustomerOrderBase>(MapUtils.getRecord(map, 'order')),
    });
  }

  toMap(context: SerializationContext): SerializedData {
    let eventId: string | null = null;

    if(OnlineEventTicketOrder.assertType(this.order) || EventTicketOrder.assertType(this.order)) {
      eventId = this.order.eventId;
    }
    if(GenericOrder.assertType(this.order)) {
      const eventIdResult = this.order.tryGetUniqueEventId();
      if(eventIdResult !== 'multiple-events-found') {
        eventId = eventIdResult.valueOrNull;
      }
    }

    return {
      'businessRef': this.businessRef.encode(),
      'donation': this.donation.encode(),
      'order': context.toData(this.order),
      'orderId': this.order.uniqueId,
      'customerId': this.order.userId,
      'isGuestOrder': this.order.isGuestOrder,
      'eventId': eventId,
      'uniqueId': this.order.uniqueId,
      'creationDate': TimeZone.encodeDate(this.order.creationDate),
    };
  }

  clone(): this {
    return DonationPlaceholder.create({
      businessRef: this.businessRef.clone(),
      donation: this.donation.clone(),
      order: this.order.clone(),
    }) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return isSameData(other.businessRef, this.businessRef) &&
      isSameData(other.donation, this.donation) &&
      isSameData(other.order, this.order);
  }
}

export class DonationPlaceholderBuilder extends DataModelBuilder<DonationPlaceholder> {
  build(context: SerializationContext, map: SerializedData): DonationPlaceholder {
    return DonationPlaceholder.fromMap(context, map);
  }
}
